import { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react'
import { UploadItem, UploadState, UploadAction, UploadStatus } from '@/types/upload'
import { useConnection } from '@/features/connection/ConnectionContext'
import { useSerialCommands } from '@/features/connection/useSerialCommands'
import { useToast } from '@/contexts/ToastContext'
import { useImageCache } from '@/hooks/useImageCache'
import { generateId } from '@/lib/utils'

interface UploadContextValue {
  queue: UploadItem[]
  isPaused: boolean
  currentUploadId: string | null
  addToQueue: (items: Omit<UploadItem, 'id' | 'status' | 'progress' | 'retryCount' | 'createdAt'>[]) => void
  removeFromQueue: (id: string) => void
  pauseAll: () => void
  resumeAll: () => void
  clearAll: () => void
  clearCompleted: () => void
  retryFailed: () => void
  retryItem: (id: string) => void
}

const UploadContext = createContext<UploadContextValue | null>(null)

export function useUpload() {
  const context = useContext(UploadContext)
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider')
  }
  return context
}

function uploadReducer(state: UploadState, action: UploadAction): UploadState {
  switch (action.type) {
    case 'ADD_ITEMS':
      return {
        ...state,
        queue: [...state.queue, ...action.items],
      }
    case 'REMOVE_ITEM':
      return {
        ...state,
        queue: state.queue.filter(item => item.id !== action.id),
        currentUploadId: state.currentUploadId === action.id ? null : state.currentUploadId,
      }
    case 'UPDATE_ITEM':
      return {
        ...state,
        queue: state.queue.map(item =>
          item.id === action.id ? { ...item, ...action.updates } : item
        ),
      }
    case 'SET_CURRENT':
      return {
        ...state,
        currentUploadId: action.id,
      }
    case 'PAUSE_ALL':
      return {
        ...state,
        isPaused: true,
      }
    case 'RESUME_ALL':
      return {
        ...state,
        isPaused: false,
      }
    case 'CLEAR_ALL':
      return {
        ...state,
        queue: [],
        currentUploadId: null,
      }
    case 'CLEAR_COMPLETED':
      return {
        ...state,
        queue: state.queue.filter(item => item.status !== 'complete'),
      }
    case 'RETRY_FAILED':
      return {
        ...state,
        queue: state.queue.map(item =>
          item.status === 'failed'
            ? { ...item, status: 'queued' as UploadStatus, error: undefined, retryCount: item.retryCount + 1 }
            : item
        ),
      }
    default:
      return state
  }
}

const initialState: UploadState = {
  queue: [],
  isPaused: false,
  currentUploadId: null,
}

interface UploadProviderProps {
  children: ReactNode
  onUploadComplete?: () => void
}

export function UploadProvider({ children, onUploadComplete }: UploadProviderProps) {
  const [state, dispatch] = useReducer(uploadReducer, initialState)
  const { isConnected } = useConnection()
  const { uploadFile } = useSerialCommands()
  const { addToast, updateToast } = useToast()
  const { setImage: cacheImage } = useImageCache()
  const processingRef = useRef(false)

  // Process queue
  const processQueue = useCallback(async () => {
    if (processingRef.current || state.isPaused || !isConnected) return

    // Find next item to process
    const nextItem = state.queue.find(
      item => item.status === 'queued' && item.processedData
    )

    if (!nextItem) return

    processingRef.current = true
    dispatch({ type: 'SET_CURRENT', id: nextItem.id })
    dispatch({ type: 'UPDATE_ITEM', id: nextItem.id, updates: { status: 'uploading' } })

    const toastId = addToast({
      type: 'progress',
      title: `Uploading ${nextItem.filename}`,
      progress: 0,
    })

    try {
      await uploadFile(
        nextItem.filename,
        nextItem.processedData!,
        (progress) => {
          dispatch({ type: 'UPDATE_ITEM', id: nextItem.id, updates: { progress } })
          updateToast(toastId, { progress })
        }
      )

      // Cache the processed image so it can be viewed without re-downloading from device
      try {
        const filename = `/${nextItem.filename}`
        // Create a proper ArrayBuffer copy from the Uint8Array
        const data = nextItem.processedData!
        const buffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer
        await cacheImage(filename, buffer)
        console.log(`[Upload] Cached image: ${filename}`)
      } catch (cacheError) {
        console.warn('[Upload] Failed to cache image:', cacheError)
      }

      dispatch({
        type: 'UPDATE_ITEM',
        id: nextItem.id,
        updates: { status: 'complete', progress: 100, completedAt: new Date() },
      })

      updateToast(toastId, {
        type: 'success',
        title: 'Upload complete',
        description: nextItem.filename,
        progress: undefined,
      })

      // Notify parent that upload completed (to refresh photo index)
      onUploadComplete?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed'
      dispatch({
        type: 'UPDATE_ITEM',
        id: nextItem.id,
        updates: { status: 'failed', error: message },
      })

      updateToast(toastId, {
        type: 'error',
        title: 'Upload failed',
        description: message,
        progress: undefined,
      })
    } finally {
      dispatch({ type: 'SET_CURRENT', id: null })
      processingRef.current = false
    }
  }, [state.queue, state.isPaused, isConnected, uploadFile, addToast, updateToast, cacheImage, onUploadComplete])

  // Auto-process queue when conditions are met
  useEffect(() => {
    if (!state.isPaused && isConnected && !processingRef.current) {
      processQueue()
    }
  }, [state.queue, state.isPaused, isConnected, processQueue])

  const addToQueue = useCallback((
    items: Omit<UploadItem, 'id' | 'status' | 'progress' | 'retryCount' | 'createdAt'>[]
  ) => {
    const newItems: UploadItem[] = items.map(item => ({
      ...item,
      id: generateId(),
      status: item.processedData ? 'queued' : 'processing',
      progress: 0,
      retryCount: 0,
      createdAt: new Date(),
    }))

    dispatch({ type: 'ADD_ITEMS', items: newItems })
  }, [])

  const removeFromQueue = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ITEM', id })
  }, [])

  const pauseAll = useCallback(() => {
    dispatch({ type: 'PAUSE_ALL' })
  }, [])

  const resumeAll = useCallback(() => {
    dispatch({ type: 'RESUME_ALL' })
  }, [])

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' })
  }, [])

  const clearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' })
  }, [])

  const retryFailed = useCallback(() => {
    dispatch({ type: 'RETRY_FAILED' })
  }, [])

  const retryItem = useCallback((id: string) => {
    dispatch({
      type: 'UPDATE_ITEM',
      id,
      updates: { status: 'queued', error: undefined, retryCount: state.queue.find(i => i.id === id)?.retryCount ?? 0 + 1 },
    })
  }, [state.queue])

  const value: UploadContextValue = {
    queue: state.queue,
    isPaused: state.isPaused,
    currentUploadId: state.currentUploadId,
    addToQueue,
    removeFromQueue,
    pauseAll,
    resumeAll,
    clearAll,
    clearCompleted,
    retryFailed,
    retryItem,
  }

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  )
}
